import { Moon, Sun } from "lucide-react";
import { isTheme, useTheme } from "@/contexts/theme";
import { THEME_OPTIONS } from "./themeOptions";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

interface ThemeToggleProps {
  size?: "default" | "large";
}

export function ThemeToggle({ size = "default" }: ThemeToggleProps) {
  const { setTheme, theme, effectiveTheme } = useTheme();

  const iconSize = size === "large" ? "!h-7 !w-7" : "h-[1.2rem] w-[1.2rem]";
  const buttonClasses = size === "large"
    ? "h-14 w-14 bg-background/80 backdrop-blur-sm shadow-lg hover:shadow-xl transition-all duration-200 hover:bg-background/90 [&_svg]:!size-7"
    : "";

  // En modo "system" se refleja el tema efectivo.
  const displayedTheme = theme === "system" ? effectiveTheme : theme;
  const iconClasses = `${iconSize} text-primary transition-all`;
  const currentLabel = THEME_OPTIONS.find((option) => option.value === theme)?.label;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={size === "large" ? "outline" : "ghost"}
          size="icon"
          className={buttonClasses}
          title="Cambiar tema"
        >
          {displayedTheme === "dark" ? (
            <Moon className={iconClasses} aria-hidden="true" />
          ) : (
            <Sun className={iconClasses} aria-hidden="true" />
          )}
          <span className="sr-only">
            Cambiar tema{currentLabel ? ` (actual: ${currentLabel})` : ""}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {/* Grupo de radio: el lector de pantalla anuncia cuál está activo; la
            antigua marca "✓" en texto no se anunciaba como seleccionada. */}
        <DropdownMenuRadioGroup
          value={theme}
          onValueChange={(value) => {
            if (isTheme(value)) setTheme(value);
          }}
        >
          {THEME_OPTIONS.map(({ value, label, icon: Icon }) => (
            <DropdownMenuRadioItem key={value} value={value} className="cursor-pointer">
              <Icon className="mr-2 h-4 w-4" aria-hidden="true" />
              <span>{label}</span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
